import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import { api } from '@/services/api'
import type { User, AuthResponse } from './types'

interface AuthContextValue {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  signup: (input: {
    email: string
    password: string
    name: string
    phone?: string
  }) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

const TOKEN_KEY = 'token'

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) {
      setLoading(false)
      return
    }
    api
      .get<{ success: boolean; data: User }>('/auth/me')
      .then((r) => {
        setUser(r.data.data)
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await api.post<{ success: boolean; data: AuthResponse }>('/auth/login', {
      email,
      password,
    })
    localStorage.setItem(TOKEN_KEY, data.data.token)
    setUser(data.data.user)
  }, [])

  const signup = useCallback(
    async (input: { email: string; password: string; name: string; phone?: string }) => {
      const { data } = await api.post<{ success: boolean; data: AuthResponse }>(
        '/auth/signup',
        input,
      )
      localStorage.setItem(TOKEN_KEY, data.data.token)
      setUser(data.data.user)
    },
    [],
  )

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    setUser(null)
  }, [])

  const value = useMemo(
    () => ({ user, loading, login, signup, logout }),
    [user, loading, login, signup, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
